import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";

const blank = {
  date: new Date().toISOString().slice(0, 10), category: "Freight / courier", amount: 0, tax: 0,
  customer_name: "", invoice_id: "", vendor: "", description: "", reference: "",
};

export const DirectCosts = ({ month, categories, onChanged }) => {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/profit/expenses");
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!open) return;
    api.get("/documents/invoices", { params: { limit: 200 } }).then((r) => setInvoices(r.data?.items || r.data || [])).catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!Number(form.amount)) return toast.error("Enter the amount.");
    if (!form.invoice_id && !form.customer_name.trim()) {
      return toast.error("Link it to an invoice or at least to a customer.");
    }
    setBusy(true);
    const payload = { ...form, amount: Number(form.amount), tax: Number(form.tax) };
    try {
      if (editId) await api.patch(`/profit/expenses/${editId}`, payload);
      else await api.post("/profit/expenses", payload);
      toast.success(editId ? "Cost updated" : "Cost added");
      setOpen(false);
      setForm(blank);
      setEditId("");
      load();
      onChanged?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/profit/expenses/${r.id}`);
      toast.success("Removed");
      load();
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="direct-costs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Costs you pay for a customer that are not already in the app - freight, outsourced drawing or
          foam cutting, courier, installation travel and so on. Anything booked to a customer without an
          invoice is shared across that customer's invoices for the month by value.
        </p>
        <Button size="sm" data-testid="add-cost-btn" onClick={() => { setEditId(""); setForm(blank); setOpen(true); }}>
          <Plus className="h-4 w-4" /> Add cost
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2.5 text-left">Date</th>
              <th className="px-3 py-2.5 text-left">Category</th>
              <th className="px-3 py-2.5 text-left">Customer / invoice</th>
              <th className="px-3 py-2.5 text-left">Details</th>
              <th className="px-3 py-2.5 text-right">Amount</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60" data-testid={`cost-row-${r.number}`}>
                <td className="px-3 py-2.5 whitespace-nowrap">{fdate(r.date)}</td>
                <td className="px-3 py-2.5">{r.category}</td>
                <td className="px-3 py-2.5">{r.customer_name || "\u2014"}
                  {r.invoice_number && <span className="num block text-xs text-muted-foreground">{r.invoice_number}</span>}
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.vendor} {r.description}</td>
                <td className="num px-3 py-2.5 text-right font-semibold">{money(Number(r.amount) + Number(r.tax || 0))}</td>
                <td className="px-3 py-2.5 text-right">
                  {can("profitability", "edit") && (
                    <Button size="iconSm" variant="ghost" data-testid={`edit-cost-${r.number}`}
                      onClick={() => {
                        setEditId(r.id);
                        setForm({
                          date: r.date, category: r.category, amount: r.amount, tax: r.tax || 0,
                          customer_name: r.customer_name || "", invoice_id: r.invoice_id || "",
                          vendor: r.vendor || "", description: r.description || "",
                          reference: r.reference || "",
                        });
                        setOpen(true);
                      }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {can("profitability", "delete") && (
                    <Button size="iconSm" variant="ghost" data-testid={`delete-cost-${r.number}`} onClick={() => remove(r)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">
                No direct costs added yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editId ? "Edit this cost" : "Add a direct cost"}</DialogTitle>
            <DialogDescription>Link it to one invoice, or to the customer to share it across their invoices.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Date" required>
                <Input type="date" value={form.date} data-testid="cost-date"
                  onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </Field>
              <Field label="Category" required>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="cost-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Invoice" hint="Leave blank to charge the customer for the month.">
                <Select value={form.invoice_id} onValueChange={(v) => {
                  const i = invoices.find((x) => x.id === v);
                  setForm({ ...form, invoice_id: v, customer_name: i?.customer_name || form.customer_name });
                }}>
                  <SelectTrigger data-testid="cost-invoice"><SelectValue placeholder="No specific invoice" /></SelectTrigger>
                  <SelectContent>
                    {invoices.map((i) => (
                      <SelectItem key={i.id} value={i.id}>{i.number} &middot; {i.customer_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Customer" required>
                <Input value={form.customer_name} data-testid="cost-customer"
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              </Field>
              <Field label="Amount" required>
                <Input type="number" min="0" step="0.01" value={form.amount} data-testid="cost-amount"
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </Field>
              <Field label="GST / tax">
                <Input type="number" min="0" step="0.01" value={form.tax} data-testid="cost-tax"
                  onChange={(e) => setForm({ ...form, tax: e.target.value })} />
              </Field>
              <Field label="Vendor">
                <Input value={form.vendor} data-testid="cost-vendor"
                  onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
              </Field>
              <Field label="Bill reference">
                <Input value={form.reference} data-testid="cost-reference"
                  onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </Field>
            </div>
            <Field label="What was it for">
              <Textarea rows={2} value={form.description} data-testid="cost-description"
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" data-testid="cost-cancel" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="cost-save">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {editId ? "Save" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
